/**
 * Owner Drawings Page (UI-9)
 *
 * Profit share table at top + drawing history below, on BE-12.
 * New Drawing dialog with overdraw warning (available = the partner's year-to-date profit share less drawings).
 * A dividend is one row ("All partners"); approve / reject / reverse act on all its parts.
 * Route: /accounting/drawings
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Alert, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, Tooltip,
} from '@mui/material';
import { Add, CheckCircle, Cancel, Undo, AttachFile, ReceiptLong } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { PeriodSelector, DataTable, StatusChip, CurrencyText } from '../../components/accounting';
import { formatDate, formatCurrency } from '../../utils/accounting/formatters';
import { getPeriodDates, toYmd } from '../../utils/accounting/periodUtils';
import { serverMessage } from '../../utils/apiConvert';
import { profitShareService } from '../../services/accounting/profitShare.service';

const RECEIPT_TYPES = 'image/*,application/pdf';

// The server lists a dividend as one drawing per partner; show it as one row.
const groupDividends = (rows) => {
  const out = [];
  const byGroup = {};
  rows.forEach((r) => {
    if (!r.dividendGroupId) { out.push(r); return; }
    const g = byGroup[r.dividendGroupId];
    if (!g) {
      byGroup[r.dividendGroupId] = { ...r, parts: [r] };
      out.push(byGroup[r.dividendGroupId]);
    } else {
      g.parts.push(r);
      g.amount = g.parts.reduce((s, p) => s + Number(p.amount || 0), 0);
      g.hasReceipt = g.parts.some((p) => p.hasReceipt);
    }
  });
  return out.map((r) => (r.parts ? { ...r, receiptId: (r.parts.find((p) => p.hasReceipt) || r.parts[0]).id } : { ...r, receiptId: r.id }));
};

const partnerCell = (r) => {
  if (!r.parts) return r.partner?.name || '—';
  return (
    <Tooltip title={r.parts.map((p) => `${p.partner?.name}: ${formatCurrency(p.amount)}`).join(' · ')}>
      <span>All partners</span>
    </Tooltip>
  );
};

const statusCell = (r) => {
  const why = r.status === 'REJECTED' ? r.rejectionReason : r.status === 'REVERSED' ? r.reversalReason : null;
  const chip = <StatusChip type="status" value={r.status} />;
  return (
    <Box>
      {why ? <Tooltip title={why}><span>{chip}</span></Tooltip> : chip}
      {r.selfApprovalException && (
        <Tooltip title="Approved by the person who recorded it — allowed because the typing center has a single admin">
          <Typography sx={{ fontSize: '0.6875rem', color: 'warning.main' }}>self-approved</Typography>
        </Tooltip>
      )}
    </Box>
  );
};

const BASE_COLUMNS = [
  { key: 'date', label: 'Date', width: '12%', render: (r) => formatDate(r.date) },
  { key: 'partner', label: 'Partner', width: '14%', render: partnerCell },
  { key: 'amount', label: 'Amount', width: '14%', align: 'right', render: (r) => <CurrencyText amount={r.amount} /> },
  { key: 'type', label: 'Type', width: '12%', render: (r) => <StatusChip type="drawingType" value={r.type} /> },
  { key: 'paymentAccount', label: 'From', width: '14%', render: (r) => r.paymentAccount?.name || '—' },
  { key: 'status', label: 'Status', width: '12%', render: statusCell },
];

const initialForm = () => ({
  partnerId: '',
  amount: '',
  type: 'DRAWING',
  paymentAccountId: '',
  date: toYmd(new Date()),
  resolutionReference: '',
  remarks: '',
});

const OwnerDrawings = ({ currentUser }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canApprove = currentUser?.role === 'TC_ADMIN';
  const [period, setPeriod] = useState('THIS_YEAR');
  const [profitShare, setProfitShare] = useState(null);
  const [yearToDate, setYearToDate] = useState(null);
  const [drawings, setDrawings] = useState({ content: [], totalElements: 0 });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [partners, setPartners] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // New drawing dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [receiptFile, setReceiptFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState(null);

  // Reject / reverse reason dialog: { action: 'reject' | 'reverse', row }
  const [reasonFor, setReasonFor] = useState(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { from, to } = getPeriodDates(period);
      const range = { from: toYmd(from), to: toYmd(to) };
      const today = new Date();
      const ytd = { from: `${today.getFullYear()}-01-01`, to: toYmd(today) };

      const [psRes, ytdRes, drRes, ptRes, acRes] = await Promise.all([
        profitShareService.getProfitShare(range),
        profitShareService.getProfitShare(ytd),
        profitShareService.getDrawings({ ...range, page, size: rowsPerPage }),
        profitShareService.getPartners(),
        profitShareService.getPaymentAccounts(),
      ]);

      setProfitShare(psRes?.data || null);
      setYearToDate(ytdRes?.data || null);
      setDrawings(drRes?.data || { content: [], totalElements: 0 });
      setPartners((ptRes?.data || []).filter((p) => p.active !== false));
      setAccounts(acRes?.data || []);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  }, [period, page, rowsPerPage]);

  useEffect(() => { loadData(); }, [loadData]);

  // A link with ?partnerId= (Team, an owner's refused salary) opens the new-drawing dialog for that partner.
  const linkedPartnerId = searchParams.get('partnerId');
  useEffect(() => {
    if (!linkedPartnerId || loading) return;
    setForm({ ...initialForm(), partnerId: linkedPartnerId });
    setReceiptFile(null);
    setDialogError(null);
    setDialogOpen(true);
    setSearchParams({}, { replace: true });
  }, [linkedPartnerId, loading, setSearchParams]);

  // Selected partner's available balance (year to date)
  const isDividend = form.type === 'DIVIDEND';
  const selectedPartner = yearToDate?.partners?.find((p) => p.id === form.partnerId);
  const availableBalance = selectedPartner ? Number(selectedPartner.available || 0) : 0;
  const formAmount = Number(form.amount) || 0;
  const isOverdraw = formAmount > 0 && formAmount > availableBalance;

  const openNew = () => {
    setForm(initialForm());
    setReceiptFile(null);
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setDialogError(null);
    try {
      const { partnerId, resolutionReference, ...rest } = form;
      const res = await profitShareService.createDrawing({
        ...rest,
        partnerId: isDividend ? undefined : partnerId,
        resolutionReference: resolutionReference.trim(),
        remarks: form.remarks.trim(),
      });
      const created = res?.data || [];
      if (receiptFile && created.length) {
        try {
          await profitShareService.attachReceipt(created[0].id, receiptFile);
        } catch (err) {
          setError(`Drawing recorded, but the receipt was not attached: ${serverMessage(err, 'upload failed')}`);
        }
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      setDialogError(serverMessage(err, 'Failed to create drawing'));
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (id) => {
    setBusyId(id);
    try {
      await profitShareService.approveDrawing(id);
      loadData();
    } catch (err) {
      setError(serverMessage(err, 'Failed to approve drawing'));
    } finally {
      setBusyId(null);
    }
  };

  const openReason = (action, row) => {
    setReasonFor({ action, row });
    setReason('');
    setReasonError(null);
  };

  const submitReason = async () => {
    const { action, row } = reasonFor;
    setBusyId(row.id);
    setReasonError(null);
    try {
      if (action === 'reject') await profitShareService.rejectDrawing(row.id, reason.trim());
      else await profitShareService.reverseDrawing(row.id, reason.trim());
      setReasonFor(null);
      loadData();
    } catch (err) {
      setReasonError(serverMessage(err, `Failed to ${action} drawing`));
    } finally {
      setBusyId(null);
    }
  };

  const attachReceipt = async (row, file) => {
    if (!file) return;
    setBusyId(row.id);
    try {
      await profitShareService.attachReceipt(row.receiptId, file);
      loadData();
    } catch (err) {
      setError(serverMessage(err, 'Failed to attach the receipt'));
    } finally {
      setBusyId(null);
    }
  };

  const openReceipt = async (row) => {
    try {
      const res = await profitShareService.getReceiptUrl(row.receiptId);
      if (res?.data?.downloadUrl) window.open(res.data.downloadUrl, '_blank', 'noopener');
    } catch (err) {
      setError(serverMessage(err, 'Could not open the receipt'));
    }
  };

  const receiptButton = (r) => (r.hasReceipt ? (
    <Tooltip title="View receipt">
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); openReceipt(r); }}>
        <ReceiptLong sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  ) : (
    <Tooltip title="Attach receipt">
      <IconButton size="small" component="label" disabled={busyId === r.id} onClick={(e) => e.stopPropagation()}>
        <AttachFile sx={{ fontSize: 18 }} />
        <input hidden type="file" accept={RECEIPT_TYPES} onChange={(e) => { attachReceipt(r, e.target.files?.[0]); e.target.value = ''; }} />
      </IconButton>
    </Tooltip>
  ));

  // Action column: approve / reject while pending, reverse once approved, receipt always
  const columnsWithActions = [
    ...BASE_COLUMNS,
    {
      key: 'actions', label: '', width: '22%', render: (r) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {canApprove && r.status === 'PENDING_APPROVAL' && (
            <>
              <Tooltip title="Approve">
                <span>
                  <IconButton size="small" color="success" disabled={busyId === r.id} onClick={(e) => { e.stopPropagation(); handleApprove(r.id); }}>
                    <CheckCircle sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Reject">
                <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); openReason('reject', r); }}>
                  <Cancel sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
          {canApprove && r.status === 'APPROVED' && (
            <Tooltip title="Reverse">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); openReason('reverse', r); }}>
                <Undo sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          {receiptButton(r)}
          {r.status !== 'PENDING_APPROVAL' && r.remarks && (
            <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', ml: 0.5 }} noWrap title={r.remarks}>{r.remarks}</Typography>
          )}
        </Box>
      ),
    },
  ];

  const rows = groupDividends(drawings.content || []);
  const reasonRow = reasonFor?.row;

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      <Typography variant="h6" sx={{ fontSize: fs.md, fontWeight: fw.semibold, mb: 1 }}>
        Owner Drawings
      </Typography>

      {/* Header row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <PeriodSelector value={period} onChange={(p) => { setPeriod(p); setPage(0); }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => navigate('/accounting/partners')}
            sx={{ textTransform: 'none', fontSize: fs.sm }}
          >
            Manage Partners
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={openNew}
            sx={{ textTransform: 'none', fontSize: fs.sm }}
          >
            New Drawing
          </Button>
        </Box>
      </Box>

      {/* Profit Share Table */}
      {profitShare && (
        <Card variant="outlined" sx={{ mb: 1.5 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, mb: 1 }}>
              Profit Share
              <Typography component="span" sx={{ fontSize: fs.xs, color: 'text.secondary', ml: 1 }}>
                Net Profit: {profitShare.netProfit == null ? '—' : formatCurrency(profitShare.netProfit)}
              </Typography>
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: fs.xs, fontWeight: fw.semibold }}>Partner</TableCell>
                    <TableCell sx={{ fontSize: fs.xs, fontWeight: fw.semibold }} align="right">Share %</TableCell>
                    <TableCell sx={{ fontSize: fs.xs, fontWeight: fw.semibold }} align="right">Profit Share</TableCell>
                    <TableCell sx={{ fontSize: fs.xs, fontWeight: fw.semibold }} align="right">Drawn</TableCell>
                    <TableCell sx={{ fontSize: fs.xs, fontWeight: fw.semibold }} align="right">Available</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {profitShare.partners.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell sx={{ fontSize: '0.8125rem' }}>{p.name}</TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem' }} align="right">{Number(p.ownershipPercent).toFixed(2)}%</TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem' }} align="right">{formatCurrency(p.profitShare)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem' }} align="right">{formatCurrency(p.drawingsTaken)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem', fontWeight: fw.semibold, color: Number(p.available) < 0 ? 'error.main' : 'success.main' }} align="right">
                        {formatCurrency(p.available)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ '& td': { fontWeight: fw.semibold, borderTop: 2, borderColor: 'divider' } }}>
                    <TableCell sx={{ fontSize: '0.8125rem' }}>Total</TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem' }} align="right">{Number(profitShare.ownershipTotal || 0).toFixed(2)}%</TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem' }} align="right">{formatCurrency(profitShare.totalProfitShare)}</TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem' }} align="right">{formatCurrency(profitShare.totalDrawings)}</TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem' }} align="right">{formatCurrency(profitShare.totalAvailable)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {(profitShare.notes || []).map((n) => (
              <Typography key={n} sx={{ fontSize: fs.xs, color: 'warning.main', mt: 1 }}>{n}</Typography>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Drawing History */}
      <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, mb: 1 }}>
        Drawing History
      </Typography>
      <DataTable
        columns={columnsWithActions}
        data={rows}
        loading={loading}
        pagination={false}
        emptyMessage="No drawings recorded for this period."
        emptyAction={{ label: 'Record your first drawing', onClick: openNew }}
      />
      {drawings.totalElements > rowsPerPage && (
        <TablePagination
          component="div"
          count={drawings.totalElements}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[20, 50, 100]}
        />
      )}

      {/* New Drawing Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>New Drawing</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!isDividend && (
              <TextField
                select
                label="Partner"
                value={form.partnerId}
                onChange={(e) => setForm({ ...form, partnerId: e.target.value })}
                size="small"
                fullWidth
              >
                {partners.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name} ({Number(p.ownershipPercent).toFixed(2)}%)</MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Amount (AED)"
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              size="small"
              fullWidth
              inputProps={{ min: 0, step: '0.01' }}
              helperText={isDividend ? 'Split across all partners by ownership %' : null}
            />
            {!isDividend && form.partnerId && (
              <Typography sx={{ fontSize: fs.xs, color: isOverdraw ? 'error.main' : 'text.secondary' }}>
                Available balance (this year): {formatCurrency(availableBalance)}
                {isOverdraw && ' — Warning: amount exceeds available balance'}
              </Typography>
            )}
            <TextField
              select
              label="Type"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              size="small"
              fullWidth
            >
              <MenuItem value="DRAWING">Drawing</MenuItem>
              <MenuItem value="DIVIDEND">Dividend</MenuItem>
            </TextField>
            <TextField
              select
              label="Pay From"
              value={form.paymentAccountId}
              onChange={(e) => setForm({ ...form, paymentAccountId: e.target.value })}
              size="small"
              fullWidth
              helperText={accounts.length ? null : 'No active bank (operations) or cash account'}
            >
              {accounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Date"
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: toYmd(new Date()) }}
            />
            {isDividend && (
              <TextField
                label="Resolution Reference"
                value={form.resolutionReference}
                onChange={(e) => setForm({ ...form, resolutionReference: e.target.value })}
                size="small"
                fullWidth
                required
                placeholder="e.g. BOD-RES-2026-Q3"
              />
            )}
            <TextField
              label="Remarks"
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              size="small"
              fullWidth
              multiline
              rows={2}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button component="label" size="small" startIcon={<AttachFile />} sx={{ textTransform: 'none', fontSize: fs.sm }}>
                {receiptFile ? 'Change receipt' : 'Attach receipt'}
                <input hidden type="file" accept={RECEIPT_TYPES} onChange={(e) => { setReceiptFile(e.target.files?.[0] || null); e.target.value = ''; }} />
              </Button>
              {receiptFile && <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }} noWrap>{receiptFile.name}</Typography>}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !form.amount || !form.paymentAccountId
              || (isDividend ? !form.resolutionReference.trim() : !form.partnerId)}
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Saving...' : 'Submit for Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject / Reverse reason dialog */}
      <Dialog open={Boolean(reasonFor)} onClose={() => setReasonFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>
          {reasonFor?.action === 'reverse' ? 'Reverse Drawing' : 'Reject Drawing'}
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {reasonError && <Alert severity="error" sx={{ mb: 2 }}>{reasonError}</Alert>}
          {reasonRow && (
            <Typography sx={{ fontSize: fs.sm, color: 'text.secondary', mb: 1.5 }}>
              {reasonRow.parts ? 'Dividend (all partners)' : reasonRow.partner?.name} · {formatCurrency(reasonRow.amount)} · {formatDate(reasonRow.date)}
              {reasonFor.action === 'reverse' && ' — a reversal entry dated today puts the money back'}
            </Typography>
          )}
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReasonFor(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            color={reasonFor?.action === 'reverse' ? 'primary' : 'error'}
            onClick={submitReason}
            disabled={!reason.trim() || busyId === reasonRow?.id}
            sx={{ textTransform: 'none' }}
          >
            {reasonFor?.action === 'reverse' ? 'Reverse' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OwnerDrawings;
